
import { Component, Vue, Prop, Emit } from "vue-property-decorator"
import {
	CalculationTableFormattingOptions,
	createDefaultCalculationTableFormattingOptions
} from "@/types/insights/formattingOptions"
import VueSelect from "vue-select"
import { Alignment, reduceAlignment } from "@/types/alignment"
import { Orientation } from "@/types/orientation"
import { SortDirection } from "@/types/sortDirection"
import { cloneDeep } from "lodash"
import api from "@/services/api"
import { DefinitionSpecialTypes } from "@/types/dataSourceItem"
import { FieldInstance } from "@/types"
import { Getter } from "vuex-class"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		"v-select": VueSelect
	}
})
export default class InsightCalculationTableFormatting extends Vue {
	/* --- Props --- */

	@Prop({ type: Object, default: null })
	private readonly value!: CalculationTableFormattingOptions | null

	@Prop({ type: Boolean, default: false })
	private readonly adHoc!: boolean

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	/* --- Data --- */

	private readonly alignmentOptions: { label: string; value: Alignment }[] = [
		{ label: "Top Left", value: Alignment.TopLeft },
		{ label: "Top", value: Alignment.Top },
		{ label: "Top Right", value: Alignment.TopRight },
		{ label: "Left", value: Alignment.Left },
		{ label: "Center", value: Alignment.Center },
		{ label: "Right", value: Alignment.Right },
		{ label: "Bottom Left", value: Alignment.BottomLeft },
		{ label: "Bottom", value: Alignment.Bottom },
		{ label: "Bottom Right", value: Alignment.BottomRight }
	]

	private readonly orientationOptions: { label: string; value: string }[] = [
		{ label: "Horizontal", value: Orientation.Horizontal },
		{ label: "Vertical", value: Orientation.Vertical }
	]

	private readonly sortDirectionOptions: { label: string; value: string | null }[] = [
		{ label: "None", value: null },
		{ label: "Ascending", value: SortDirection.Ascending },
		{ label: "Descending", value: SortDirection.Descending }
	]

	private userHeaderFields: FieldInstance[] = []

	private TenantFeatures = TenantFeatures

	/* --- Computed properties --- */

	private get defaultOptions (): CalculationTableFormattingOptions {
		return createDefaultCalculationTableFormattingOptions()
	}

	private get headingsAlignment (): Alignment {
		return this.value?.headingsAlignment ??
			this.defaultOptions.headingsAlignment
	}

	private set headingsAlignment (value: Alignment) {
		const currentValue = this.setDefaultValueIfNull()
		const newValue = cloneDeep(currentValue)
		newValue.headingsAlignment = value
		this.setValue(newValue)
	}

	private get headingsOrientation (): string {
		return this.value?.verticalHeadings ??
				this.defaultOptions.verticalHeadings
			? Orientation.Vertical
			: Orientation.Horizontal
	}

	private set headingsOrientation (value: string) {
		const currentValue = this.setDefaultValueIfNull()
		const newValue = cloneDeep(currentValue)
		newValue.verticalHeadings = value === Orientation.Vertical

		if (!newValue.verticalHeadings) {
			// update headings alignment if no longer applicable
			newValue.headingsAlignment = reduceAlignment(newValue.headingsAlignment, false)
		}

		this.setValue(newValue)
	}

	private get headingsSortDirection (): SortDirection | null {
		return this.value?.columnsSort ??
			this.defaultOptions.columnsSort
	}

	private set headingsSortDirection (value: SortDirection | null) {
		const currentValue = this.setDefaultValueIfNull()
		const newValue = cloneDeep(currentValue)
		newValue.columnsSort = value
		this.setValue(newValue)
	}

	private get valuesAlignment (): Alignment {
		return this.value?.valuesAlignment ??
			this.defaultOptions.valuesAlignment
	}

	private set valuesAlignment (value: Alignment) {
		const currentValue = this.setDefaultValueIfNull()
		const newValue = cloneDeep(currentValue)
		newValue.valuesAlignment = value
		this.setValue(newValue)
	}

	private get displayUserHeaderFields (): boolean {
		return this.value?.displayUserHeaderFields ??
			this.defaultOptions.displayUserHeaderFields
	}

	private set displayUserHeaderFields (value: boolean) {
		const currentValue = this.setDefaultValueIfNull()
		const newValue = cloneDeep(currentValue)
		newValue.displayUserHeaderFields = value
		if (!newValue.displayUserHeaderFields) {
			newValue.excludeUserHeaderFields = []
		}
		this.setValue(newValue)
	}

	private get horizontalAlignmentOptions (): { label: string; value: Alignment }[] {
		return [
			this.alignmentOptions.find(option => option.value === Alignment.Left)!,
			this.alignmentOptions.find(option => option.value === Alignment.Center)!,
			this.alignmentOptions.find(option => option.value === Alignment.Right)!
		]
	}

	private get availableHeadingAlignmentOptions (): { label: string; value: Alignment }[] {
		return this.value?.verticalHeadings
			? this.alignmentOptions
			: this.horizontalAlignmentOptions
	}

	private get excludeUserHeaderFields (): string[] {
		return this.value?.excludeUserHeaderFields ?? []
	}

	private set excludeUserHeaderFields (value: string[]) {
		const currentValue = this.setDefaultValueIfNull()
		const newValue = cloneDeep(currentValue)
		newValue.excludeUserHeaderFields = value
		this.setValue(newValue)
	}

	private get userDefinitionFieldOptions (): { label: string; value: string }[] {
		return this.userHeaderFields ? this.userHeaderFields.map(u => ({ label: u.field.title, value: u.field.id || "" })) : []
	}

	/* --- Vue-related methods --- */

	@Emit("input")
	private setValue (value: CalculationTableFormattingOptions | null): CalculationTableFormattingOptions | null {
		return value
	}

	/* --- Methods & Lifecycle hooks --- */

	public async mounted () {
		let userDefinition = await api.specialDefinitionWithFields(DefinitionSpecialTypes.User)
		this.userHeaderFields = userDefinition.fields
		if (this.value && this.value.excludeUserHeaderFields) {
			let newValue = cloneDeep(this.value)
			let filteredExcludeUserHeaderFields = newValue.excludeUserHeaderFields.filter(userHeaderField => userDefinition.fields.map(field => field.field.id).includes(userHeaderField))
			this.excludeUserHeaderFields = filteredExcludeUserHeaderFields
		}
	}

	private setDefaultValueIfNull (): CalculationTableFormattingOptions {
		if (!this.value) {
			const newValue = createDefaultCalculationTableFormattingOptions()
			this.setValue(newValue)
			return newValue
		}

		return this.value
	}

	public resetToDefaults (): void {
		this.setValue(createDefaultCalculationTableFormattingOptions())
	}

	public resetAdHocToInsightSettings (): void {
		if (!this.adHoc) {
			console.error("This method should only be called for ad-hoc formatting settings")
			return
		}

		this.setValue(null)
	}
}
